/**
 * ErrorBubble (PHASE 7)
 * =====================
 * Chat bubble renderer for universal error schema.
 *
 * Requirements:
 * - Icon + title + summary
 * - "See details" drawer hook
 * - Retry button hook (caller supplies handler + label)
 */

import React from 'react';

import { AlertTriangle, RotateCcw, ChevronRight } from 'lucide-react';

import { summarizeUniversalError } from '@/pages/intelligence-studio/utils/universalError';

export default function ErrorBubble({
  error,
  onRetry,
  retryLabel = 'Retry',
  onViewDetails,
  className = '',
}) {
  const s = (() => {
    try {
      return summarizeUniversalError(error);
    } catch {
      return { title: 'Error', summary: 'An unexpected error occurred' };
    }
  })();

  const ts = (() => {
    try {
      const v = error?.timestamp;
      if (!v) return null;
      const d = new Date(String(v));
      if (!Number.isFinite(d.getTime())) return String(v);
      return d.toLocaleString();
    } catch {
      return null;
    }
  })();

  return (
    <div
      className={`rounded-2xl rounded-tl-sm px-4 py-3 border shadow-sm bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-slate-800 dark:text-neutral-100 ${className}`}
      data-testid="istudio-error-bubble"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 p-1.5 rounded-lg bg-red-100 dark:bg-red-900/30">
          <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-red-800 dark:text-red-200 truncate">
            {s.title}
          </div>
          <div className="mt-1 text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap break-words">
            {s.summary}
          </div>
          {error?.code ? (
            <div className="mt-2 text-[10px] font-mono text-red-700/80 dark:text-red-300/80">
              code: {String(error.code)}
              {error?.httpStatus ? ` · http: ${String(error.httpStatus)}` : ''}
            </div>
          ) : null}
          {ts ? (
            <div className="mt-1 text-[10px] text-red-700/70 dark:text-red-300/70">
              {ts}
            </div>
          ) : null}

          <div className="mt-3 flex items-center gap-2">
            {typeof onRetry === 'function' && (
              <button
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium"
                onClick={onRetry}
                data-testid="istudio-error-retry"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {retryLabel}
              </button>
            )}
            {typeof onViewDetails === 'function' && (
              <button
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-red-300 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-700 dark:text-red-200 text-xs font-medium"
                onClick={onViewDetails}
                data-testid="istudio-error-details"
              >
                See details
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

